import {
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';

import {
  FormBuilderComponent,
  getValidators
} from '@shared/components/row-edition-form/form-builder/form-builder.component';
import {
  FormBuilderFieldType,
  FormBuilderItem,
  FormBuilderSchema,
  FormBuilderSection,
  FormGroupOptional,
  OneOfField
} from '@shared/components/row-edition-form/form-builder/form-builder.model';

import {
  FormMode,
  rememberLoadedArrayItem,
  transformFormValue
} from './row-edition-form.utils';

export interface FormBuilderRowResult {
  [property: string]:
    | string
    | Date
    | boolean
    | null
    | FormBuilderRowResult
    | FormBuilderRowResult[];
}

@Component({
  selector: 'app-row-edition-form',
  imports: [ReactiveFormsModule, FormBuilderComponent],
  templateUrl: './row-edition-form.component.html'
})
export class RowEditionFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  protected form!: FormGroup;
  private formStatus = signal<FormControlStatus>('INVALID');
  /** Row the form was last seeded for, so a re-emitted load is told apart from a new row. */
  private patchedRowId?: string;

  schema = input.required<FormBuilderSchema>();
  rowId = input.required<string>();
  actualValues = input<{ [key: string]: unknown }>();
  formMode = input<FormMode>('create');
  /** Entity type of the row being edited (e.g. 'site'); used to scope proposed labels. */
  entityType = input<string>('');

  formValueChange = output<FormBuilderRowResult>();
  formValidChange = output<boolean>();

  ngOnInit() {
    this.form = this.createFormGroup(this.schema(), this.actualValues());
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.formStatus.set(status);
        this.formValidChange.emit(status === 'VALID');
      });
    this.setupBlurEmission();
  }

  private setupBlurEmission(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.form.touched) {
        this.emitCurrentValues();
      }
    });
  }

  private emitCurrentValues(): void {
    const formValue = this.form.value;
    const transformedValue = this.transformFormValue(formValue, this.schema());
    this.formValueChange.emit(transformedValue);
  }

  /** Get current form values transformed for submission */
  getCurrentValues(): FormBuilderRowResult | null {
    if (this.form.valid) {
      const formValue = this.form.value;
      return this.transformFormValue(formValue, this.schema());
    }
    this.form.markAllAsTouched();
    return null;
  }

  /** Check if form is currently valid */
  isValid(): boolean {
    return this.form.valid;
  }

  constructor() {
    effect(() => {
      const values = this.actualValues();
      const rowId = this.rowId();
      if (!values || !this.form) {
        return;
      }
      // The loaded entity seeds the form; it is not a live feed. Re-applying it once
      // the user has started editing overwrites their input with the persisted value,
      // and this effect re-runs on every re-emission of the load. Switching to another
      // row is a new subject, so it still takes the form over.
      if (this.form.dirty && rowId === this.patchedRowId) {
        return;
      }
      this.patchedRowId = rowId;
      this.form.patchValue(values);
    });
  }

  /**
   * Builds the control tree for one schema level.
   *
   * `currentValues` must hold the values of *this* level only. Falling back to the
   * whole entity when a level has no value seeds a section's leaves from same-named
   * root properties — an unset `load_travel_cost` picked up the vehicle's own
   * `cost_per_kilometer`, which also made the optional section look filled and come
   * up enabled.
   */
  private createFormGroup(
    schema: FormBuilderSchema | FormBuilderSection,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentValues?: any
  ): FormGroup {
    const group: { [key: string]: FormControl | FormGroup | FormArray } = {};

    for (const item of schema.items) {
      if ('type' in item) {
        if (item.type === FormBuilderFieldType.MULTIPLAY) {
          const sectionGroup = this.tryCreateMultiplaySectionGroup(
            item,
            currentValues?.[item.id]
          );
          if (sectionGroup) {
            group[item.id] = sectionGroup;
            continue;
          }
        }
        const validators = getValidators(item);
        group[item.id] = new FormControl(currentValues?.[item.id], validators);
      } else if ('items' in item) {
        if (item.isArray) {
          group[item.id] = this.createFormArray(item, currentValues?.[item.id]);
        } else {
          group[item.id] = this.createFormGroup(item, currentValues?.[item.id]);
          (group[item.id] as FormGroupOptional).isRequired = item.required;
          if (!item.required && !this.formGroupHasAnyValue(group[item.id])) {
            group[item.id].disable();
          }
        }
      }
    }

    return this.fb.group(group);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createFormArray(schema: FormBuilderSection, arrayValues?: any[]): FormArray {
    const formArray = this.fb.array<FormGroup>([]);

    if (Array.isArray(arrayValues) && arrayValues.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      arrayValues.forEach((value: any) => {
        const formGroup = this.createFormGroup(schema, value);
        rememberLoadedArrayItem(formGroup, value);
        formArray.push(formGroup);
      });
    }

    return formArray;
  }

  /**
   * Builds the control for an externally-tagged enum (MULTIPLAY) whose variants
   * all carry section payloads (object or vector), e.g. the Job `job_visits`
   * field `{ job_visit_patterns: UserInnerJobRecurringVisit[] }`. The MULTIPLAY value is a
   * single-key envelope `{ <wrapperProperty>: <payload> }`, so we model it as a
   * FormGroup keyed by each variant id, reusing the recursive, value-aware
   * createFormArray/createFormGroup so nested arrays load correctly in edit mode.
   *
   * Returns null for scalar or mixed (scalar + section) MULTIPLAYs, which keep
   * the flat FormControl handled by the leaf components / tagged-section.
   */
  private tryCreateMultiplaySectionGroup(
    item: OneOfField,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ): FormGroup | null {
    // oneOf is typed FieldList[], but the transformer also emits section
    // variants (object/vector payloads) into it for externally-tagged enums.
    const oneOf = (item.oneOf ?? []) as unknown as FormBuilderItem[];
    const sectionVariants = oneOf.filter(
      (variant): variant is FormBuilderSection => !('type' in variant) && 'items' in variant
    );
    if (sectionVariants.length === 0 || sectionVariants.length !== oneOf.length) {
      return null;
    }

    const group: { [key: string]: FormGroup | FormArray } = {};
    for (const variant of sectionVariants) {
      const inner =
        value && typeof value === 'object' && !Array.isArray(value)
          ? value[variant.id]
          : undefined;
      group[variant.id] = variant.isArray
        ? this.createFormArray(variant, inner)
        : this.createFormGroup(variant, inner);
    }
    return this.fb.group(group);
  }

  private transformFormValue(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    schema: FormBuilderSchema | FormBuilderSection
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    return transformFormValue(value, schema, this.form, this.actualValues());
  }

  private formGroupHasAnyValue(control: AbstractControl): boolean {
    if (control instanceof FormControl) {
      const value = control.value;
      return (
        (value !== null && value !== undefined && value !== '') ||
        (typeof value === 'string' && value.trim() !== '')
      );
    }
    if (control instanceof FormGroup) {
      return Object.values(control.controls).some((c) => this.formGroupHasAnyValue(c));
    }
    if (control instanceof FormArray) {
      return control.controls.some((c) => this.formGroupHasAnyValue(c));
    }
    return false;
  }
}
